import { RoleOptions } from '@contexts/users/domain';

import type { BreadcrumbConfig } from '@components/molecules/breadcrumbs/models/breadcrumb';
import type { DynamicOptionsParams } from '@components/molecules/breadcrumbs/services/breadcrumbs.service';

import { routesMetadata } from '@shared/models';
import type { AccessContext, AccessStrategy, WidgetConfig } from '@shared/services';

export class ClientAccessStrategy implements AccessStrategy {
	getWidgets(ctx: AccessContext): WidgetConfig[] {
		const widgets: WidgetConfig[] = [
			{
				id: 'devices',
				label: 'entities.devices.title',
				routerLink: routesMetadata.devicesB2C.relative,
				icon: 'device'
			},
			{
				id: 'contacts',
				label: 'entities.contacts.title',
				routerLink: routesMetadata.contacts.relative,
				icon: 'contact'
			},
			{
				id: 'user-gallery',
				label: 'entities.userGallery.title',
				routerLink: routesMetadata.userGallery.relative,
				icon: 'image'
			}
		];

		if (ctx.hasLocationCapability) {
			widgets.push(
				{
					id: 'latest-position',
					label: 'entities.latestPosition.title',
					routerLink: routesMetadata.latestPosition.absolute,
					icon: 'latestPosition'
				},
				{
					id: 'recorded-positions',
					label: 'entities.recordedPositions.title',
					routerLink: routesMetadata.recordedPositions.absolute,
					icon: 'recordedPositions'
				},
				{
					id: 'frequent-places',
					label: 'entities.frequentPlaces.title',
					routerLink: routesMetadata.frequentPlaces.absolute,
					icon: 'frequentPlaces'
				}
			);
		}

		return widgets;
	}

	private buildBaseBreadcrumbs(): BreadcrumbConfig {
		return {
			levels: [
				{
					id: 'section',
					controlsSegmentAt: 0,
					options: [
						{
							id: 'devices',
							label: 'entities.devices.title',
							segment: routesMetadata.devicesB2C.relative,
							navigable: true
						},
						{
							id: 'contacts',
							label: 'entities.contacts.title',
							segment: routesMetadata.contacts.relative,
							navigable: true
						},
						{
							id: 'user-gallery',
							label: 'entities.userGallery.title',
							segment: routesMetadata.userGallery.relative,
							navigable: true
						}
					]
				},
				{
					id: 'devices-ids',
					controlsSegmentAt: 1,
					visibleWhen: {
						rootSegmentIs: routesMetadata.devicesB2C.relative,
						segmentAtIs: {
							index: 1,
							values: []
						}
					},
					options: []
				},
				{
					id: 'device-tabs',
					controlsSegmentAt: 2,
					visibleWhen: {
						rootSegmentIs: routesMetadata.devicesB2C.relative
					},
					options: [
						{
							id: routesMetadata.configuration.relative,
							label: 'entities.devices.configuration.tab',
							segment: routesMetadata.configuration.relative,
							navigable: true
						},
						{
							id: routesMetadata.callHistories.relative,
							label: 'entities.callHistories.title',
							segment: routesMetadata.callHistories.relative,
							navigable: true
						},
						{
							id: routesMetadata.contacts.relative,
							label: 'entities.devices.contactLists.tab',
							segment: routesMetadata.contacts.relative,
							navigable: true
						},
						{
							id: routesMetadata.deviceUsers.relative,
							label: 'entities.devices.usersList.title',
							segment: routesMetadata.deviceUsers.relative,
							navigable: true
						},
						{
							id: routesMetadata.latestPosition.relative,
							label: 'entities.latestPosition.title',
							segment: routesMetadata.latestPosition.relative,
							navigable: true
						},
						{
							id: routesMetadata.recordedPositions.relative,
							label: 'entities.recordedPositions.title',
							segment: routesMetadata.recordedPositions.relative,
							navigable: true
						},
						{
							id: routesMetadata.frequentPlaces.relative,
							label: 'entities.frequentPlaces.title',
							segment: routesMetadata.frequentPlaces.relative,
							navigable: true
						},
						{
							id: routesMetadata.safeZone.relative,
							label: 'entities.safeZones.title',
							segment: routesMetadata.safeZone.relative,
							navigable: true
						},
						{
							id: routesMetadata.health.relative,
							label: 'entities.devices.actions.health',
							segment: routesMetadata.health.relative,
							navigable: true
						},
						{
							id: routesMetadata.scheduledActivities.relative,
							label: 'entities.devices.scheduledActivities.tab',
							segment: routesMetadata.scheduledActivities.relative,
							navigable: true
						},
						{
							id: routesMetadata.alarms.relative,
							label: 'entities.devices.alarms.tab',
							segment: routesMetadata.alarms.relative,
							navigable: true
						},
						{
							id: routesMetadata.apps.relative,
							label: 'entities.devices.installedApps.title',
							segment: routesMetadata.apps.relative,
							navigable: true
						},
						{
							id: routesMetadata.photoGallery.relative,
							label: 'entities.photoGallery.title',
							segment: routesMetadata.photoGallery.relative,
							navigable: true
						}
					]
				},
				{
					id: 'apps-actions',
					controlsSegmentAt: 3,
					visibleWhen: {
						rootSegmentIs: 'devices-b2c',
						segmentAtIs: {
							index: 2,
							values: ['apps']
						}
					},
					options: [
						{
							id: 'usage',
							label: 'entities.devices.usageApps.title',
							segment: 'usage',
							navigable: false
						}
					]
				},
				{
					id: 'frequent-places-actions',
					controlsSegmentAt: 3,
					visibleWhen: {
						rootSegmentIs: 'devices-b2c',
						segmentAtIs: {
							index: 2,
							values: ['frequent-places']
						}
					},
					options: [
						{
							id: 'create',
							label: 'entities.frequentPlaces.pages.create.title',
							segment: 'create',
							navigable: false
						}
					]
				}
			]
		};
	}

	async getBreadcrumbs(
		accessContext: AccessContext
	): Promise<[BreadcrumbConfig, DynamicOptionsParams[]]> {
		const { findAllDevicesUseCase } = accessContext;
		const baseConfig = this.buildBaseBreadcrumbs();

		const devices = await findAllDevicesUseCase({});

		if (devices.total === 0) return [baseConfig, []];

		const devicesDynamicOptions: DynamicOptionsParams = {
			levelId: 'devices-ids',
			options: devices.items.map((device) => ({
				id: device.id,
				label: device.carrierName,
				segment: device.id,
				navigable: true
			})),
			preserveChildrenInGroupId: 'device-ids'
		};
		return [baseConfig, [devicesDynamicOptions]];
	}

	canAccess(roles: string[]): boolean {
		return roles.includes(RoleOptions.CLIENT);
	}
}
